import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import SecondaryButton from "../SecondaryButton/SecondaryButton";
import CONFIG from '../../config/config';
import { useTranslation } from 'react-i18next';
import { CardV2, CardContent } from '../CardV2/CardV2';
import Title from "../Title/Title";
import OnboardingAPI from '../../api/OnboardingAPI';
import { toastService } from '../../services/ToastService';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import useOnboardingQueryPresets, { OnboardingStep as OnboardingQueryStep,} from "../../hooks/useOnboardingQueryPresets";

interface MultipleChoiceOption {
  value: string;
  label: string;
}

const OnboardingFlow: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";

  const { steps: filteredSteps, presetAnswers } = useOnboardingQueryPresets(CONFIG.ONBOARDING_QUESTIONS as OnboardingQueryStep[], location.search);
  const steps = filteredSteps as OnboardingQueryStep[];
  
  const [stepIndex, setStepIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<string, any>>(() => ({ ...presetAnswers, }));
  const [loading, setLoading] = useState(false);

  const languageSwitcher = <LanguageSwitcher className="mt-2" />;

  const endQuestionnaire = useCallback(async (finalAnswers = answers) => {
    setFade(false);
    setLoading(true);
    try {
      await OnboardingAPI.saveAnswers(finalAnswers);
    } catch (e) {
      toastService.showToast(t('global.error.instructions') || 'An error occurred while saving your answers.', 'error');
    } finally {
      setTimeout(() => {
        setLoading(false);
        if (finalAnswers.calendar_preset === "yes") {
          navigate("/onboarding/booking", { state: { from: redirectTo } });
        } else {
          navigate(redirectTo);
        }
      }, 500);
    }
  }, [answers, navigate, redirectTo, t]);

  const getNextUnansweredIndex = useCallback(
    (currentIdx: number, currentAnswers: Record<string, any>) => {
      for (let idx = currentIdx + 1; idx < steps.length; idx += 1) {
        const step = steps[idx];
        if (currentAnswers[step.answerKey] === undefined) {
          return idx;
        }
      }
      return null;
    },
    [steps]
  );

  const goToNextAvailableStep = useCallback(
    (currentIdx: number, currentAnswers: Record<string, any>) => {
      const nextIndex = getNextUnansweredIndex(currentIdx, currentAnswers);
      if (nextIndex === null) {
        endQuestionnaire(currentAnswers);
        return;
      }
      setFade(false);
      setTimeout(() => {
        setStepIndex(nextIndex);
        setFade(true);
        setSelectedOptions([]);
      }, 500);
    },
    [endQuestionnaire, getNextUnansweredIndex]
  );

  const handleNext = () => {
    const currentStep = steps[stepIndex];
    if (!currentStep) return;

    let updatedAnswers: Record<string, any> = answers;
    if (currentStep.type === 'multiple-choice') {
      const existingAnswer = answers[currentStep.answerKey];
      if (existingAnswer === undefined) {
        updatedAnswers = { ...answers, [currentStep.answerKey]: selectedOptions };
        setAnswers(updatedAnswers);
      }
    }

    goToNextAvailableStep(stepIndex, updatedAnswers);
  };

  const handleAnswer = (answer: string) => {
    const currentStep = steps[stepIndex];
    if (!currentStep) return;

    let updatedAnswers: Record<string, any> = answers;
    const existingAnswer = answers[currentStep.answerKey];
    if (existingAnswer === undefined) {
      updatedAnswers = { ...answers, [currentStep.answerKey]: answer };
      setAnswers(updatedAnswers);
    }

    goToNextAvailableStep(stepIndex, updatedAnswers);
  };

  const handleOptionChange = (value: string) => {
    setSelectedOptions((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const currentStep = steps[stepIndex];
  if (!currentStep) return null;
  const multipleChoiceOptions = currentStep.options as
    | MultipleChoiceOption[]
    | undefined;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-darker-gold-50 via-white to-as-pink-100">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-darker-gold-200 rounded-full opacity-10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-as-pink-300 rounded-full opacity-10 blur-3xl" />
      </div>

      <CardV2 className="w-full max-w-4xl shadow-2xl border-0 bg-white/95 backdrop-blur-sm rounded-3xl relative z-10">
        <CardContent className="p-8">
          <div className={`transition-all duration-500 ease-in-out ${fade ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex justify-center mb-8">
              <div className="flex space-x-2">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 w-8 rounded-full transition-all duration-300 ${
                      index <= stepIndex 
                        ? 'bg-darker-gold-300 shadow-lg' 
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Title with improved typography */}
            <div className="text-center mb-8">
              <Title 
                text={t(currentStep.title || '') as string} 
                clazz="text-3xl font-bold text-darker-gold-default mb-2 text-center" 
              />
              {currentStep.type === 'intro' && (
                <p className="text-lg text-gray-600 mb-4">
                  {t('onboarding.intro.subtitle')}
                </p>
              )}
              <div className="h-1 w-32 bg-gradient-to-r from-darker-gold-300 to-as-pink-300 rounded-full mx-auto" />
            </div>

            {/* Loading state */}
            {loading && (
              <div className="text-center text-darker-gold-300 mb-6 font-medium">
                {t('global.loading') || 'Saving...'}
              </div>
            )}

            {/* Content sections */}
            {currentStep.type === 'intro' && (
              <div className="flex flex-col items-center space-y-8">
                <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
                  {t(currentStep.description || '') as string}
                </p>
                <PrimaryButton
                  text={t(currentStep.primaryButton || '') as string}
                  fn={handleNext}
                  actionName="onboarding_intro_next"
                />
                {languageSwitcher}
              </div>
            )}

            {currentStep.type === 'question' && (
              <div className="flex flex-col items-center space-y-8">
                <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
                  {t(currentStep.description || '') as string}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md justify-center">
                  <SecondaryButton
                    text={t(currentStep.secondaryButton || 'global.skip') as string}
                    fn={() => handleAnswer("skip")}
                    actionName="onboarding_skip"
                  />
                  <PrimaryButton
                    text={t(currentStep.primaryButton || '') as string}
                    fn={() => handleAnswer("yes")}
                    actionName="onboarding_yes_interested"
                  />
                </div>
                {languageSwitcher}
              </div>
            )}

            {currentStep.type === 'multiple-choice' && (
              <div className="flex flex-col items-center space-y-8">
                <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
                  {t(currentStep.description || '') as string}
                </p>
                
                {/* Enhanced checkbox styling */}
                <div className="w-full max-w-2xl space-y-3">
                  {multipleChoiceOptions?.map((option) => (
                    <label 
                      key={option.value} 
                      className="flex items-center p-4 rounded-xl border-2 border-gray-200 hover:border-darker-gold-200 hover:bg-darker-gold-50 transition-all duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        value={option.value}
                        checked={selectedOptions.includes(option.value)}
                        onChange={() => handleOptionChange(option.value)}
                        className="w-5 h-5 accent-darker-gold-300 border-gray-300 rounded focus:ring-darker-gold-200 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700 group-hover:text-darker-gold-DEFAULT transition-colors duration-200">
                        {t(option.label || '') as string}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md justify-center">
                  <SecondaryButton
                    text={t(currentStep.secondaryButton || 'global.skip') as string}
                    fn={() => handleAnswer("skip")}
                    actionName="onboarding_skip"
                  />
                  <PrimaryButton
                    text={t(currentStep.primaryButton || '') as string}
                    fn={handleNext}
                    actionName="onboarding_multiple_choice_next"
                    isDisabled={selectedOptions.length === 0}
                  />
                </div>
                {languageSwitcher}
              </div>
            )}
          </div>
        </CardContent>
      </CardV2>
    </div>
  );
};

export default OnboardingFlow; 